// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import type {
  TeamSize,
  WizardData,
  WizardProgress,
  WorkRole,
} from "./models/onboardingModels";

export type WizardStepId =
  | "welcome"
  | "contacts"
  | "communications"
  | "esign"
  | "properties"
  | "team"
  | "done";

export interface WizardStepDefinition {
  id: WizardStepId;
  label: string;
  minutes: number;
}

export function buildWizardSteps(
  role: WorkRole | null,
  teamSize: TeamSize | null,
  teamsEnabled: boolean,
): WizardStepDefinition[] {
  const steps: WizardStepDefinition[] = [
    { id: "welcome", label: "About You", minutes: 5 },
    { id: "contacts", label: "Your Contacts", minutes: 3 },
    { id: "communications", label: "Communications", minutes: 10 },
    { id: "esign", label: "E-Signature", minutes: 3 },
  ];
  if (role === "property" || role === "both") {
    steps.push({ id: "properties", label: "Properties", minutes: 10 });
  }
  if (teamsEnabled && teamSize && teamSize !== "solo") {
    steps.push({ id: "team", label: "Your Team", minutes: 5 });
  }
  steps.push({ id: "done", label: "Done", minutes: 0 });
  return steps;
}

export function isExplicitInProgressWizard(
  progress: WizardProgress | null | undefined,
): boolean {
  if (!progress || progress.complete === true) {
    return false;
  }

  const hasStepId =
    typeof progress.stepId === "string" && progress.stepId.length > 0;
  const hasStepIndex =
    typeof progress.stepIndex === "number" &&
    Number.isFinite(progress.stepIndex) &&
    progress.stepIndex >= 0;

  return hasStepId || hasStepIndex;
}

export function resolveWorkspaceOnboarded(input: {
  hasTenantMembership: boolean;
  settings?: Partial<WizardData> | null | undefined;
}): boolean {
  const progress = input.settings?.wizardProgress;
  if (isExplicitInProgressWizard(progress)) {
    return false;
  }

  if (progress?.complete === true) {
    return true;
  }

  return input.hasTenantMembership;
}

function resumeTargetStepId(steps: readonly { id: string }[], stepId: string): string {
  const teamStepMissing = stepId === "team" && !steps.some((step) => step.id === "team");
  return teamStepMissing ? "done" : stepId;
}

export function resolveResumeStepIndex(
  steps: readonly { id: string }[],
  progress: WizardProgress | null | undefined,
): number {
  if (!progress) {
    return 0;
  }

  if (progress.complete === true) {
    const doneIndex = steps.findIndex((step) => step.id === "done");
    return doneIndex >= 0 ? doneIndex : 0;
  }

  if (!isExplicitInProgressWizard(progress)) {
    return 0;
  }

  if (typeof progress.stepId === "string" && progress.stepId.length > 0) {
    const targetId = resumeTargetStepId(steps, progress.stepId);
    const byId = steps.findIndex((step) => step.id === targetId);
    if (byId >= 0) {
      return byId;
    }
  }

  if (
    typeof progress.stepIndex === "number" &&
    progress.stepIndex >= 0 &&
    progress.stepIndex < steps.length
  ) {
    return progress.stepIndex;
  }

  return 0;
}

export function withWizardProgress(
  data: WizardData,
  requestedIndex: number,
  teamsEnabled: boolean,
): WizardData {
  const steps = buildWizardSteps(data.role, data.teamSize, teamsEnabled);
  const lastIndex = Math.max(steps.length - 1, 0);
  const stepIndex = Math.max(0, Math.min(requestedIndex, lastIndex));
  const step = steps[stepIndex];

  return {
    ...data,
    wizardProgress: {
      stepIndex,
      stepId: step?.id ?? "welcome",
      complete: step?.id === "done",
    },
  };
}

export function mergeWizardSnapshot(
  current: WizardData,
  loaded: Partial<WizardData>,
): WizardData {
  return {
    ...current,
    ...loaded,
    role: loaded.role ?? current.role,
    teamSize: loaded.teamSize ?? current.teamSize,
    importSource: loaded.importSource ?? current.importSource,
    importedContacts: loaded.importedContacts ?? current.importedContacts,
    mergeCandidates: loaded.mergeCandidates ?? current.mergeCandidates,
  };
}
